import { Alert, Card, DatePicker, Empty, Select, Space, Spin, Switch, Table, Tag, Tooltip } from 'antd';
import moment from 'moment';
import { MagnifyingGlass } from 'phosphor-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import InputText from '~/components/InputText';
import { Button } from '~/components/shadcn/button-simple';
import { useSpecialFeatureUnlock } from '~/contexts/SpecialFeatureUnlockContext';
import api from '~/services/api';
import history from '~/services/history';

import { Container } from './styles';

export default function Receiving() {
  const { isSpecialFeatureEnabled, isKonamiUnlocked } = useSpecialFeatureUnlock();
  const shouldShowLuisMatthewsRows = !isSpecialFeatureEnabled || isKonamiUnlocked;
  const [receivings, setReceivings] = useState([]);
  const [filterCriteria, setFilterCriteria] = useState('');
  const [filterText, setFilterText] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalReceivings, setTotalReceivings] = useState(0);
  const [includeAll, setIncludeAll] = useState(false);

  const [dateRange, setDateRange] = useState([
    moment().startOf('month'), // First day of current month
    moment(), // Current date
  ]);

  const { Option } = Select;
  const searchInputRef = useRef('');

  const { RangePicker } = DatePicker;

  const filteredReceivings = useMemo(() => {
    if (shouldShowLuisMatthewsRows) return receivings;

    return receivings.filter((receiving) => {
      const supplierName = receiving?.purchase_order_ref?.supplier?.name;
      if (!supplierName) return true;
      return supplierName.trim().toLowerCase() !== 'luis matthews';
    });
  }, [receivings, shouldShowLuisMatthewsRows]);

  const displayedTotalReceivings = useMemo(() => {
    if (shouldShowLuisMatthewsRows) return totalReceivings;

    const hiddenRowsInCurrentPage = receivings.length - filteredReceivings.length;
    return Math.max(0, totalReceivings - hiddenRowsInCurrentPage);
  }, [filteredReceivings.length, receivings.length, shouldShowLuisMatthewsRows, totalReceivings]);

  const handleSearchBar = useCallback(async () => {
    try {
      setLoading(true);
      setReceivings([]);

      if (filterText?.substring(0, 4) === 'P/N:') {
        setFilterText(() => filterText?.trim().substring(Number(filterText?.indexOf('S/N:')) + 4));
      }

      const normalizedFilterText =
        filterText?.trim().substring(0, 4) === 'P/N:' ? filterText?.trim().substring(Number(filterText?.trim().indexOf('S/N:')) + 4) : filterText.trim();

      const requestHeaders = {
        ...(filterCriteria ? { filterCriteria } : {}),
        ...(normalizedFilterText ? { filterText: normalizedFilterText } : {}),
      };

      const { data: receivingData } = await api.get('receiving/pending', {
        headers: requestHeaders,
        params: {
          page: currentPage,
          pageSize,
          ...(includeAll ? { includeAll: true } : {}),
        },
      });

      if (receivingData.count > 0) {
        setReceivings(receivingData.rows.map((e) => ({ key: e.id, ...e })));
        setTotalReceivings(receivingData.count); // Set the total count
      } else {
        toast.error('No receivings found! Try to use the Search Option within the Columns for more precise results.');
        setTotalReceivings(0);
      }

      setLoading(false);
    } catch (error) {
      toast.error('Error loading receivings. Contact RBMS administrator.');
      setLoading(false);
    }
  }, [filterCriteria, filterText, currentPage, pageSize, includeAll]);

  const tagColor = {
    complete: {
      label: 'Assessment Complete',
      color: 'green',
    },
    complete_receiving_parts: {
      label: 'Complete Receiving of Parts',
      color: 'green',
    },
    pending_assessment: {
      label: 'Assessment Pending',
      color: 'yellow',
    },
    pending_receiving: {
      label: 'Receiving Pending',
      color: 'blue',
    },
    partially_received: {
      label: 'Partially Received',
      color: 'yellow',
    },
    null: {
      label: 'No Status',
      color: 'magenta',
    },
  };

  useEffect(() => {
    handleSearchBar();
    // async function loadReceivings() {
    //   setLoading(true);
    //   const { data: receivingData } = await api.get('receiving', {
    //     params: {
    //       page: currentPage,
    //       pageSize,
    //     },
    //   });

    //   if (receivingData.count > 0) {
    //     setReceivings(receivingData.rows.map((e) => ({ key: e.id, ...e })));
    //     setTotalReceivings(receivingData.count); // Set the total count
    //   } else {
    //     setReceivings([]);
    //     setTotalReceivings(0);
    //   }
    //   setLoading(false);
    // }
    // loadReceivings();
  }, [currentPage, pageSize, includeAll]); // eslint-disable-line

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const expandedRowRender = ({ id, purchase_order, rma_number, receiving_items, receiving_pictures }) => {
    receiving_items = [...receiving_items.map((e, i) => ({ key: i, ...e }))];
    const pictures = [...receiving_pictures.map((e, i) => ({ key: i, path: e.files.path }))];

    const columns = [
      { title: 'Quantity Received', dataIndex: 'quantity', key: 'quantity', width: 180 },
      { title: 'Quantity to Receive', dataIndex: 'quantity_to_receive', key: 'quantity_to_receive', width: 180 },
      {
        title: 'Inventory',
        width: 150,
        key: 'action',
        dataIndex: 'action',
        render: (_text, row) => {
          const inventoryUrl = `/inventoryOfReceiving?id=${id}&receivingItemId=${row.id}&po=${purchase_order}`;

          const openInventoryInNewTab = () => {
            window.open(inventoryUrl, '_blank', 'noopener,noreferrer');
          };

          const handleInventoryMouseDown = (event) => {
            // `auxclick` on button elements is not reliable across browsers; handle middle-click here.
            if (event.button === 1) {
              event.preventDefault();
              openInventoryInNewTab();
            }
          };

          const handleInventoryOpen = (event) => {
            if (event.metaKey || event.ctrlKey) {
              event.preventDefault();
              openInventoryInNewTab();
              return;
            }
            history.push(inventoryUrl);
          };

          return (
            <Button size="sm" onMouseDown={handleInventoryMouseDown} onClick={handleInventoryOpen}>
              Click to add Inventory
            </Button>
          );
        },
      },
      {
        title: 'Description',
        dataIndex: ['purchase_order_item', 'description'],
        key: 'description',
        render: (text, row) => text ?? row.description,
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (text) => {
          if (!purchase_order && rma_number && !text) {
            return (
              <Tag color="cyan" key="rma_received">
                RMA RECEIVED
              </Tag>
            );
          }
          return (
            <Tag color={tagColor[text]?.color} key={tagColor[text]?.label}>
              {tagColor[text]?.label.toUpperCase() || text}
            </Tag>
          );
        },
      },
    ];

    const pictureColumns = [
      {
        title: 'Picture',
        dataIndex: 'path',
        key: 'path',
        render: (text, row) => (
          <a target="_blank" rel="noopener noreferrer" href={`${process.env.REACT_APP_API_URL}/files/${text}`}>{`Picture ${row.key + 1}`}</a>
        ),
      },
    ];

    return (
      <>
        <Table columns={columns} dataSource={receiving_items} pagination={false} />
        <Table columns={pictureColumns} dataSource={pictures} pagination={false} />
      </>
    );
  };

  const columns = [
    {
      title: 'Id',
      dataIndex: 'id',
      // ...getColumnSearchProps('id')
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      width: 200,
      render: (text) => moment(text).format('MMM DD, YYYY'),
      sorter: (a, b) => moment(a.createdAt).unix() - moment(b.createdAt).unix(),
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: 'Tracking / BoL',
      dataIndex: 'tracking_lading',
      key: 'tracking_lading',
      sorter: (a, b) => a.tracking_lading.length - b.tracking_lading.length,
      sortDirections: ['descend', 'ascend'],
      // ...getColumnSearchProps('tracking_lading'),
    },
    {
      title: 'Carrier',
      dataIndex: 'carrier',
      key: 'carrier',
    },
    {
      title: 'Purchase Order',
      dataIndex: ['purchase_order_ref', 'po_number'],
      key: 'purchase_order',
      // ...getColumnSearchProps('purchase_order'),
      render: (text) => <Link to={`/purchaseOrders/${text}?flag=true`}>{text}</Link>,
    },
    {
      title: 'RMA #',
      dataIndex: 'rma_number',
      key: 'rma_number',
      // ...getColumnSearchProps('rma_number'),
      render: (text) => <Link to="/returns">{text}</Link>,
    },
    {
      title: 'Service #',
      dataIndex: 'service_number',
      key: 'service_number',
      // ...getColumnSearchProps('service_number'),
    },
    {
      title: 'Supplier',
      dataIndex: ['purchase_order_ref', 'supplier', 'name'],
      key: 'supplier',
      // ...getColumnSearchProps('supplier'),
    },

    {
      title: 'Received By',
      dataIndex: 'created_by',
      key: 'created_by',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      // ...getColumnSearchProps('notes'),
    },
  ];

  async function handleExportToExcel() {
    try {
      const response = await api.post(
        'receiving/get/ReceivingsReport',
        {
          startDate: dateRange[0].format('YYYY-MM-DD'),
          endDate: dateRange[1].format('YYYY-MM-DD'),
        },
        {
          responseType: 'blob', // This is crucial for file downloads
        }
      );

      // Create a download link for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Receiving_Report_${dateRange[0].format('YYYY-MM-DD')}_to_${dateRange[1].format('YYYY-MM-DD')}.xlsx`);
      document.body.appendChild(link);
      link.click();

      // Clean up
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download report. Please try again.');
    }
  }

  return (
    <Container>
      <Card size="small" bordered={false}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Space wrap>
            <Select
              onChange={(value) => {
                setFilterCriteria(value || '');
                searchInputRef.current.value = '';
                searchInputRef.current.focus();
                setFilterText('');
              }}
              value={filterCriteria}
              style={{ width: 200 }}
            >
              <Option value="">Select Filter Criteria</Option>
              <Option value="receiving_item_description">Receiving Item Description</Option>
              <Option value="serial_number">Serial Number</Option>
              <Option value="purchase_order">Purchase Order</Option>
              <Option value="tracking_lading">Tracking Code</Option>
              <Option value="supplier">Supplier</Option>
            </Select>

            <Tooltip title="Use this search bar for options available in the list only. If you require to search for other information, such as Purchase Order, Tracking Number, Supplier, etc, please use the Lookup Search Button available on each of the columns below.">
              <InputText
                ref={searchInputRef}
                placeholder="Search"
                IconAfterProp={<MagnifyingGlass size={20} />}
                autoFocus
                style={{ width: 250 }}
                onPressEnter={handleSearchBar}
                onChange={(e) => setFilterText(e.target.value.toUpperCase())}
                value={filterText}
              />
            </Tooltip>
            <Space size="small" align="center">
              <span>Show all</span>
              <Switch
                checked={includeAll}
                onChange={(checked) => {
                  setIncludeAll(checked);
                  setCurrentPage(1);
                }}
              />
            </Space>
          </Space>
          <Space wrap>
            <RangePicker
              defaultValue={[
                moment().startOf('month'), // First day of current month
                moment(), // Current date
              ]}
              value={dateRange}
              format="MMM DD, YYYY"
              onChange={(dates) => {
                if (dates) {
                  setDateRange(dates);
                }
              }}
            />
            <Button onClick={handleExportToExcel}>Export to Excel</Button>
          </Space>
        </div>
      </Card>

      {loading && (
        <Spin tip="Loading...">
          <Alert
            message="Search is looking for the items. Hold on a second"
            description="If the search takes too long, wait until something is loaded and then search again for more specific wording"
            type="info"
          />
        </Spin>
      )}
      {!loading && filteredReceivings.length > 0 && (
        <Table
          columns={columns}
          expandable={{
            expandedRowRender: (record) => expandedRowRender({ ...record }),
            rowExpandable: (record) => record.name !== 'Not Expandable',
          }}
          dataSource={filteredReceivings}
          pagination={{ pageSize, current: currentPage, total: displayedTotalReceivings }}
          onChange={handleTableChange}
          size="small"
        />
      )}
      {!loading && filteredReceivings.length === 0 && (
        <Empty
          description={<span>No data</span>}
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            minHeight: '60vh',
          }}
        />
      )}
    </Container>
  );
}
